import React, { useState } from "react";
import "./SearchResults.css";

/**
 * SearchResults Component
 * Componente para exibir resultados de pesquisas
 * @version 1.0.0
 */

const SearchResults = ({ results, query }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("relevance");

  if (!results) {
    return <div className="no-results">Nenhum resultado encontrado</div>;
  }

  // Contar resultados
  const exerciseCount = results.exercises?.length || 0;
  const userCount = results.users?.length || 0;
  const blogCount = results.blogs?.length || 0;
  const lookbookCount = results.lookbooks?.length || 0;
  const totalCount = results.total || 0;

  // Filtrar resultados baseado na seleção
  let displayResults = [];
  if (activeFilter === "all") {
    displayResults = [
      ...results.exercises.map((e) => ({ ...e, type: "exercise" })),
      ...results.users.map((u) => ({ ...u, type: "user" })),
      ...results.blogs.map((b) => ({ ...b, type: "blog" })),
      ...results.lookbooks.map((l) => ({ ...l, type: "lookbook" })),
    ];
  } else if (activeFilter === "exercise") {
    displayResults = results.exercises.map((e) => ({ ...e, type: "exercise" }));
  } else if (activeFilter === "user") {
    displayResults = results.users.map((u) => ({ ...u, type: "user" }));
  } else if (activeFilter === "blog") {
    displayResults = results.blogs.map((b) => ({ ...b, type: "blog" }));
  } else if (activeFilter === "lookbook") {
    displayResults = results.lookbooks.map((l) => ({ ...l, type: "lookbook" }));
  }

  return (
    <div className="search-results">
      {/* Header com contadores */}
      <div className="results-header">
        <h2>Resultados para "{query}"</h2>
        <p className="results-count">
          {totalCount} resultados encontrados em {results.time}
        </p>
      </div>

      {/* Filtros */}
      <div className="results-filters">
        <button
          className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
          onClick={() => setActiveFilter("all")}
        >
          Todos ({totalCount})
        </button>
        {exerciseCount > 0 && (
          <button
            className={`filter-btn ${activeFilter === "exercise" ? "active" : ""}`}
            onClick={() => setActiveFilter("exercise")}
          >
            💪 Exercícios ({exerciseCount})
          </button>
        )}
        {userCount > 0 && (
          <button
            className={`filter-btn ${activeFilter === "user" ? "active" : ""}`}
            onClick={() => setActiveFilter("user")}
          >
            👤 Usuários ({userCount})
          </button>
        )}
        {blogCount > 0 && (
          <button
            className={`filter-btn ${activeFilter === "blog" ? "active" : ""}`}
            onClick={() => setActiveFilter("blog")}
          >
            📝 Blogs ({blogCount})
          </button>
        )}
        {lookbookCount > 0 && (
          <button
            className={`filter-btn ${activeFilter === "lookbook" ? "active" : ""}`}
            onClick={() => setActiveFilter("lookbook")}
          >
            👗 Lookbooks ({lookbookCount})
          </button>
        )}

        {/* Ordenação */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="sort-select"
        >
          <option value="relevance">Mais relevantes</option>
          <option value="recent">Mais recentes</option>
          <option value="popular">Mais populares</option>
        </select>
      </div>

      {/* Lista de Resultados */}
      <div className="results-list">
        {displayResults.length > 0 ? (
          displayResults.map((item, idx) => (
            <ResultItem key={`${item.type}-${idx}`} item={item} />
          ))
        ) : (
          <div className="no-results">
            Nenhum resultado encontrado para "{query}" nesta categoria
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Componente para exibir um item individual de resultado
 */
const ResultItem = ({ item }) => {
  const [liked, setLiked] = useState(false);

  // Renderizar baseado no tipo
  switch (item.type) {
    case "exercise":
      return (
        <div className="result-item exercise-item">
          {item.image && (
            <img src={item.image} alt={item.title} className="result-image" />
          )}
          <div className="result-content">
            <h3 className="result-title">{item.title}</h3>
            <p className="result-description">{item.description}</p>
            <div className="result-meta">
              <span className="badge category">{item.category}</span>
              <span className="badge difficulty">{item.difficulty}</span>
              <span className="badge level">{item.level}</span>
            </div>
          </div>
          <div className="result-actions">
            <button onClick={() => setLiked(!liked)} className="action-btn">
              {liked ? "❤️" : "🤍"} {liked ? "Curtido" : "Curtir"}
            </button>
            <button className="action-btn">📌 Salvar</button>
          </div>
        </div>
      );

    case "user":
      return (
        <div className="result-item user-item">
          {item.avatar && (
            <img src={item.avatar} alt={item.username} className="result-avatar" />
          )}
          <div className="result-content">
            <h3 className="result-title">{item.fullname}</h3>
            <p className="result-username">@{item.username}</p>
            <p className="result-description">{item.bio}</p>
          </div>
          <div className="result-actions">
            <button className="action-btn follow-btn">👤 Seguir</button>
            <button className="action-btn">💬 Mensagem</button>
          </div>
        </div>
      );

    case "blog":
      return (
        <div className="result-item blog-item">
          <div className="result-content">
            <h3 className="result-title">{item.title}</h3>
            {item.author && (
              <p className="result-author">
                Por <strong>{item.author.username}</strong>
              </p>
            )}
            <div className="result-meta">
              <span className="badge">📝 Blog</span>
            </div>
          </div>
          <div className="result-actions">
            <button onClick={() => setLiked(!liked)} className="action-btn">
              {liked ? "❤️" : "🤍"} {liked ? "Curtido" : "Curtir"}
            </button>
            <button className="action-btn">📖 Ler</button>
          </div>
        </div>
      );

    case "lookbook":
      return (
        <div className="result-item lookbook-item">
          {item.cover && (
            <img src={item.cover} alt={item.title} className="result-image" />
          )}
          <div className="result-content">
            <h3 className="result-title">{item.title}</h3>
            <p className="result-description">{item.description}</p>
            <div className="result-meta">
              <span className="badge">👗 Lookbook</span>
            </div>
          </div>
          <div className="result-actions">
            <button onClick={() => setLiked(!liked)} className="action-btn">
              {liked ? "❤️" : "🤍"} {liked ? "Curtido" : "Curtir"}
            </button>
            <button className="action-btn">👁️ Ver</button>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default SearchResults;
